import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Flame, CheckCircle2, Eye, EyeOff } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      <div className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: `url('https://popmenucloud.com/cdn-cgi/image/width=1200,height=1200,fit=scale-down,format=auto,quality=60/jkweqdot/f7f92250-a632-48be-b5df-753100fdb36a.jpg')`
      }} />
      <div className="absolute inset-0 bg-black/50" />
      <Card className="relative z-10 w-full max-w-md mx-4 border-stone-200 shadow-2xl bg-white/95 backdrop-blur-sm rounded-2xl" data-testid="reset-password-card">
        <CardHeader className="text-center pb-2 pt-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-12 h-12 bg-[#1B2A4A] rounded-xl flex items-center justify-center">
              <Flame className="w-7 h-7 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold tracking-tight text-stone-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Set New Password
          </CardTitle>
        </CardHeader>
        <CardContent className="px-6 pb-8 pt-2">
          {!success ? (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-password-form">
              {!token && <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">Missing reset token. Please use the link from your email.</div>}
              {error && <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="reset-error">{error}</div>}
              <div className="space-y-2">
                <Label>New Password</Label>
                <div className="relative">
                  <Input type={showPassword ? "text" : "password"} value={password}
                    onChange={(e) => setPassword(e.target.value)} placeholder="Min 6 characters"
                    className="rounded-xl border-stone-200 h-11 pr-10" data-testid="new-password-input" required />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Confirm Password</Label>
                <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter password" className="rounded-xl border-stone-200 h-11"
                  data-testid="confirm-password-input" required />
              </div>
              <Button type="submit" disabled={loading || !token}
                className="w-full h-11 bg-[#1B2A4A] text-white rounded-xl font-medium hover:bg-[#243658]"
                data-testid="reset-submit-btn">
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          ) : (
            <div className="text-center space-y-4" data-testid="reset-success">
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto" />
              <p className="text-stone-700 font-medium">Password reset successfully!</p>
              <Link to="/login">
                <Button className="bg-[#1B2A4A] text-white rounded-xl hover:bg-[#243658]" data-testid="go-to-login-btn">
                  Sign In with New Password
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
